import React, { useState, useEffect, useCallback } from 'react';
import { Field, reduxForm } from 'redux-form';
import { connect } from 'react-redux';
import { Form, Nav, NavDropdown } from 'react-bootstrap';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';
import { injectIntl, FormattedMessage } from 'react-intl';

import HeaderModal from '../../HeaderModal/HeaderModal';
import Loader from '../../Common/Loader';
import NavLink from '../../NavLink';

import { choseToTheme } from '../../../actions/currency/getCurrencyRates';
import { openHeaderModal } from '../../../actions/siteadmin/modalActions';
import { formatLocale } from '../../../helpers/formatLocale';

import submit from './submit';
import validate from './validate';
import messages from '../../../locale/messages';

import { api, logoUploadDir } from '../../../config';
import {
  AdminHeaderDarkModeIcon, AdminHeaderGoToMainSiteIcon, AdminHeaderLanguageIcon,
  AdminHeaderLightModeIcon
} from '../../Common/Assets/SVGIcon/AdminHeaderIcon';

import adminLoginImage from '/public/static/adminLogin.svg';
import bt from '../../../components/commonStyle.css';
import s from './AdminLoginForm.css';

const renderField = ({ input, label, type, meta: { touched, error }, placeholder, formatMessage }) => (
  <Form.Group>
    <label className={cx(bt.commonLabelText)}>{label}</label>
    <Form.Control
      {...input}
      placeholder={placeholder}
      type={type}
      className={bt.formControlInput}
    />
    {touched && error && (
      <span className={bt.errorMessage}>{formatMessage(error)}</span>
    )}
  </Form.Group>
);

const AdminLoginForm = (props) => {
  const { error, handleSubmit, submitting, loading, logo, intl: { formatMessage }, selectedTheme, currentLocale, openHeaderModal, choseToTheme, siteName } = props;
  const [isLoad, setIsLoad] = useState(true);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      setIsLoad(false);
    }
  }, []);

  const renderFieldWithIntl = useCallback((fieldProps) => {
    return renderField({ ...fieldProps, formatMessage });
  }, [formatMessage]);

  const handleChange = (e) => {
    openHeaderModal('languageModal');
  }

  return (
    <>
      <HeaderModal />
      <div className={s.loginMainBg}>
        <div className={cx(s.bgPadding, 'adminLoginLsBoxRtl')}>
          <div className={s.loginBg} style={{ backgroundImage: `url(${adminLoginImage})` }} />
        </div>
        <div className={s.leftPadding}>
          <header className={s.adminLogInHeaderContainer}>
            <Nav className={s.adminLogInHeaderNavMenuContainer}>
              <NavDropdown title={
                <>
                  <span className={cx(s.adminLogInHeaderMenuItem, s.adminLogInHeaderDropdownMenuItem)}>
                    <span className={s.adminLogInHeaderMenuItemIconBox}>
                      {selectedTheme === 'light' && <AdminHeaderLightModeIcon />}
                      {selectedTheme === 'dark' && <AdminHeaderDarkModeIcon />}
                    </span>
                    <span className={s.adminLogInHeaderMenuItemText}>
                      {selectedTheme === 'light' && <FormattedMessage {...messages.lightMode} />}
                      {selectedTheme === 'dark' && <FormattedMessage {...messages.darkMode} />}
                    </span>
                  </span>
                </>
              } id="basic-nav-dropdown2" className={"adminHeaderDropdownMenu"}>
                <NavDropdown.Item to="#" onClick={() => { choseToTheme('light') }}>
                  <div className={cx(s.adminLogInHeaderDropdownMenuInnerWrapper, selectedTheme === 'light' && s.adminLogInHeaderDropdownMenuSelect)}>
                    <AdminHeaderLightModeIcon />
                    <FormattedMessage {...messages.lightMode} />
                  </div>
                </NavDropdown.Item>
                <NavDropdown.Item to="#" onClick={() => { choseToTheme('dark') }}>
                  <div className={cx(s.adminLogInHeaderDropdownMenuInnerWrapper, selectedTheme === 'dark' && s.adminLogInHeaderDropdownMenuSelect)}>
                    <AdminHeaderDarkModeIcon />
                    <FormattedMessage {...messages.darkMode} />
                  </div>
                </NavDropdown.Item>
              </NavDropdown>
              <div onClick={(e) => handleChange(e)} tabIndex={0} className={s.adminLogInHeaderMenuItem}>
                <span className={s.adminLogInHeaderMenuItemIconBox}>
                  <AdminHeaderLanguageIcon className={s.adminLogInHeaderMenuItemIcon} />
                </span>
                <span className={s.adminLogInHeaderMenuItemText}>
                  {formatLocale(currentLocale)}
                </span>
              </div>
              <NavLink to={'/'} className={s.adminLogInHeaderMenuItem}>
                <span className={s.adminLogInHeaderMenuItemIconBox}>
                  <AdminHeaderGoToMainSiteIcon className={s.adminLogInHeaderMenuItemIcon} />
                </span>
                <span className={s.adminLogInHeaderMenuItemText}>
                  <FormattedMessage {...messages.goToMainSite} />
                </span>
              </NavLink>
            </Nav>
          </header>
          <div className={s.formInner}>
            <div className={s.loginTitleSection}>
              {logo ? (
                <img src={`${api.apiEndpoint}${logoUploadDir}${logo}`} />
              ) : (
                <p className={s.loginTitleText}>{siteName}</p>
              )}
              <p className={cx(bt.titleText, bt.fontWeight600, bt.paddingTop2)}>
                <FormattedMessage {...messages.welcomeAdminLabel} />
              </p>
            </div>
            <Form className={s.fullWidth} onSubmit={handleSubmit(submit)}>
              {error && (
                <span className={bt.errorMessage}>{error}</span>
              )}
              <Field
                name="email"
                type="text"
                component={renderFieldWithIntl}
                label={formatMessage(messages.email)}
                placeholder={formatMessage(messages.emailaddress)}
                labelClass={bt.commonLabelText}
                fieldClass={cx(bt.formControlInput)}
              />
              <Field
                name="password"
                type="password"
                component={renderFieldWithIntl}
                label={formatMessage(messages.password)}
                placeholder={formatMessage(messages.password)}
                labelClass={bt.commonLabelText}
                fieldClass={cx(bt.formControlInput)}
              />
              <div className={s.loginButton}>
                <Loader
                  type="button"
                  label={formatMessage(messages.login)}
                  show={loading}
                  buttonType="submit"
                  className={cx(bt.btnPrimary, 'loaderButtonFlex')}
                  disabled={submitting || loading || isLoad}
                />
              </div>
            </Form>
          </div>
        </div>
      </div>
    </>
  );
};

const mapState = (state) => ({
  loading: state.loader.AdminLogin,
  siteName: state.siteSettings.data.siteName,
  logo: state.siteSettings.data.homeLogo,
  selectedTheme: state?.currency?.theme,
  currentLocale: state?.intl?.locale,
});

const mapDispatch = {
  openHeaderModal,
  choseToTheme
}

export default injectIntl(
  withStyles(bt, s)(
    connect(mapState, mapDispatch)(
      reduxForm({
        form: 'AdminLoginForm',
        validate,
        onSubmit: submit,
      })(AdminLoginForm)
    )
  )
);
